"use client";

import {useEffect, useRef, useState} from "react";

/**
 * Reports whether a ref'd element has entered the viewport. Used to defer
 * expensive preview work (fetch, parse, canvas render) until the user is
 * actually looking at the element. Once true, stays true so scrolling back
 * doesn't re-trigger the work. SSR-safe — returns `false` on the server.
 */
export function useInView<T extends Element>(
   rootMargin?: string
): [React.RefObject<T | null>, boolean] {
   const ref = useRef<T | null>(null);
   const [inView, setInView] = useState(false);

   useEffect(() => {
      const node = ref.current;
      if (!node) return;
      // Fall back to "always visible" when IO is unavailable (very old envs,
      // some test setups). Matches current behavior for callers.
      if (typeof IntersectionObserver === "undefined") {
         setInView(true);
         return;
      }
      const observer = new IntersectionObserver(
         (entries) => {
            for (const entry of entries) {
               if (entry.isIntersecting) {
                  setInView(true);
                  observer.disconnect();
               }
            }
         },
         {rootMargin}
      );
      observer.observe(node);
      return () => observer.disconnect();
   }, [rootMargin]);

   return [ref, inView];
}
